import { useQuery } from '@apollo/client'
import { useCallback, useMemo, useState } from 'react'

import { FETCH_BULK_COLLECTION_FILTERED } from '../../../../lib/graphql/queries/FETCH_BULK_COLLECTION_FILTERED'
import { BULK_COLLECTION_HANDLE } from '../constants'
import { GenderKey, OrganizedCategories, PageInfo, Product, VisualFilterTile } from '../types'

export const useFetchBulk = ({
  gender,
  categoryTile,
}: {
  gender: GenderKey
  categoryTile: VisualFilterTile
}) => {
  const [organizedData, setOrganizedData] = useState<OrganizedCategories>({ men: [], women: [] })

  const currentCategory = useMemo(
    () => organizedData[gender].find(category => category.value === categoryTile.value),
    [organizedData, gender, categoryTile.value]
  )

  const updateCategoryData = useCallback(
    (newProducts: Product[], newPageInfo: PageInfo) => {
      setOrganizedData(prev => {
        const categories = prev[gender]
        const categoryIndex = categories.findIndex(item => item.value === categoryTile.value)

        const updatedCategory = {
          ...categoryTile,
          products: newProducts,
          pageInfo: newPageInfo,
        }

        const updatedCategories =
          categoryIndex === -1
            ? [...categories, updatedCategory]
            : categories.map((cat, i) => (i === categoryIndex ? updatedCategory : cat))

        return { ...prev, [gender]: updatedCategories }
      })
    },
    [gender, categoryTile]
  )

  const { data, error, fetchMore, loading } = useQuery(FETCH_BULK_COLLECTION_FILTERED, {
    variables: {
      handle: BULK_COLLECTION_HANDLE,
      selectedFilters: [
        { key: 'category', values: [categoryTile.value] },
        { key: 'gender', values: [gender, 'unisex'] },
      ],
    },
    notifyOnNetworkStatusChange: true,
    skip: !!currentCategory,
    onCompleted: data => {
      if (!data?.optimizedCollection?.items?.nodes) return

      updateCategoryData(
        data.optimizedCollection.items.nodes,
        data.optimizedCollection.items.pageInfo
      )
    },
  })

  const handleFetchMore = async (options: { variables: { after: string } }) => {
    const result = await fetchMore({
      variables: options.variables,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        const newNodes = [
          ...(prev.optimizedCollection?.items?.nodes || []),
          ...(fetchMoreResult.optimizedCollection?.items?.nodes || []),
        ]

        return {
          optimizedCollection: {
            ...fetchMoreResult.optimizedCollection,
            id: fetchMoreResult.optimizedCollection?.id || '',
            items: {
              ...fetchMoreResult.optimizedCollection?.items,
              nodes: newNodes,
              pageInfo: fetchMoreResult?.optimizedCollection?.items?.pageInfo ?? {
                hasNextPage: false,
                endCursor: null,
              },
            },
          },
        }
      },
    })

    if (result.data?.optimizedCollection?.items) {
      const { nodes, pageInfo } = result.data.optimizedCollection.items
      const existingProducts = currentCategory?.products || []
      updateCategoryData([...existingProducts, ...nodes], pageInfo)
    }

    return result
  }

  return {
    data: data?.optimizedCollection?.items?.nodes,
    categorizedData: organizedData[gender],
    pageInfo: currentCategory?.pageInfo || data?.optimizedCollection?.items?.pageInfo,
    loading,
    error,
    fetchMore: handleFetchMore,
  }
}
